import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { FaSave, FaImage, FaPalette, FaFileAlt, FaArrowLeft } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';

function PortalBranding() {
  const { portalId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Branding state
  const [branding, setBranding] = useState({
    logo: '',
    primaryColor: '#2186EB',
    secondaryColor: '#FF7417',
    customMessage: '',
    showPoweredBy: true
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check if user has Pro or Team plan
    if (user?.plan !== 'pro' && user?.plan !== 'team') {
      navigate('/pricing');
      return;
    }

    fetchPortal();
  }, [portalId, isAuthenticated, user]);

  const fetchPortal = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/portals/${portalId}`);
      setPortal(response.data.portal);
      if (response.data.portal.branding) {
        setBranding({
          ...branding,
          ...response.data.portal.branding
        });
      }
    } catch (err) {
      setError('Failed to load portal');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setBranding({
        ...branding,
        logo: reader.result
      });
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      await axios.put(`${API_URL}/api/portals/${portalId}`, {
        branding
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = () => {
    setBranding({
      ...branding,
      logo: ''
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2186EB] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!portal) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Portal Not Found</h2>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-[#2186EB] hover:underline"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <FaArrowLeft /> Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Customize Portal Branding</h1>
          <p className="text-gray-600 mt-2">
            Customize how your intake portal appears to visitors. Only available for Pro and Team plans.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
            Branding saved successfully!
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Settings Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSave} className="bg-white rounded-lg shadow-lg p-6 space-y-6">
              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaImage className="inline mr-2" />
                  Portal Logo
                </label>
                {branding.logo ? (
                  <div className="mb-4">
                    <img
                      src={branding.logo}
                      alt="Portal logo"
                      className="max-h-32 mb-2 rounded"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Remove logo
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <FaImage className="text-4xl text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 mb-2">Upload a logo image</p>
                    <label className="inline-block px-4 py-2 bg-[#2186EB] text-white rounded-lg cursor-pointer hover:bg-blue-700 transition">
                      Choose File
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-gray-500 mt-2">Max 2MB, PNG or JPG</p>
                  </div>
                )}
                {!branding.logo && (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="mt-2"
                  />
                )}
              </div>

              {/* Primary Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaPalette className="inline mr-2" />
                  Primary Color
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="color"
                    value={branding.primaryColor}
                    onChange={(e) => setBranding({ ...branding, primaryColor: e.target.value })}
                    className="w-20 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={branding.primaryColor}
                    onChange={(e) => setBranding({ ...branding, primaryColor: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="#2186EB"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Used for buttons and accents</p>
              </div>

              {/* Secondary Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaPalette className="inline mr-2" />
                  Secondary Color
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="color"
                    value={branding.secondaryColor}
                    onChange={(e) => setBranding({ ...branding, secondaryColor: e.target.value })}
                    className="w-20 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={branding.secondaryColor}
                    onChange={(e) => setBranding({ ...branding, secondaryColor: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="#FF7417"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Used for highlights and secondary elements</p>
              </div>

              {/* Custom Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaFileAlt className="inline mr-2" />
                  Custom Welcome Message
                </label>
                <textarea
                  value={branding.customMessage}
                  onChange={(e) => setBranding({ ...branding, customMessage: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2186EB]"
                  placeholder="Add a custom message that will appear at the top of your portal..."
                />
                <p className="text-xs text-gray-500 mt-1">Optional message to display to visitors</p>
              </div>

              {/* Show Powered By */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showPoweredBy"
                  checked={branding.showPoweredBy}
                  onChange={(e) => setBranding({ ...branding, showPoweredBy: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="showPoweredBy" className="text-sm text-gray-700">
                  Show "Powered by MirageDocs" footer
                </label>
              </div>

              {/* Save Button */}
              <button
                type="submit"
                disabled={saving}
                className="w-full py-3 bg-[#2186EB] text-white rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <FaSave /> {saving ? 'Saving...' : 'Save Branding'}
              </button>
            </form>
          </div>

          {/* Preview */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-4">
              <h3 className="font-semibold text-gray-900 mb-4">Preview</h3>
              <div
                className="border-2 border-gray-200 rounded-lg p-6"
                style={{
                  backgroundColor: '#f9fafb'
                }}
              >
                {/* Preview Portal */}
                <div className="text-center mb-4">
                  {branding.logo && (
                    <img
                      src={branding.logo}
                      alt="Logo"
                      className="max-h-16 mx-auto mb-4"
                    />
                  )}
                  <h2
                    className="text-2xl font-bold mb-2"
                    style={{ color: branding.primaryColor }}
                  >
                    {portal.name}
                  </h2>
                  {branding.customMessage && (
                    <p className="text-sm text-gray-600 mb-4">{branding.customMessage}</p>
                  )}
                </div>
                <div
                  className="px-4 py-2 rounded-lg text-white text-center text-sm font-semibold mb-4"
                  style={{ backgroundColor: branding.primaryColor }}
                >
                  Upload File Button
                </div>
                {branding.showPoweredBy && (
                  <p className="text-xs text-center text-gray-400 mt-4">
                    Powered by MirageDocs
                  </p>
                )}
              </div>
              <div className="mt-4 text-center">
                <a
                  href={`${window.location.origin}/receive/${portalId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#2186EB] hover:underline"
                >
                  View Live Portal →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PortalBranding;

